import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenCheckIcon, ClipboardCheckIcon, Clock3Icon, HeartPulseIcon, SparklesIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCheckoutUrl, getStudyUrl } from '../../../utils/studyAccess';
import { useStudyMembership } from '../../../utils/useStudyMembership';
const PROGRAM_ID = 'rd-ready-cnsc-prep' as const;
const studyFeatures = [{
  icon: HeartPulseIcon,
  title: 'CNSC-focused learning',
  description: 'Review nutrition support concepts in a clear, focused study environment.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Intentional practice',
  description: 'Use structured study sessions to turn complex material into steady progress.'
}, {
  icon: Clock3Icon,
  title: 'Study on your terms',
  description: 'Return to your purchased program whenever it works for you.'
}];
export function CNSCExam() {
  const membership = useStudyMembership(PROGRAM_ID);
  const hasAccess = membership.hasAccess;
  return <div className="min-h-screen w-full bg-sky-50 text-slate-900" data-id="element-2044" data-mp="3djtoqpy2jwsv">
      <section className="overflow-hidden border-b border-sky-200 bg-[#e8f8f6]" data-id="element-2045" data-mp="38omqhxnkjkbc">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:gap-16 lg:py-24" data-id="element-2046" data-mp="3nd11wwz1wh3i">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} data-id="element-2047" data-mp="3ij6blaoxx3vg">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#b7efe4] px-4 py-2 text-sm font-bold text-slate-900" data-id="element-2048" data-mp="a4xpmlgxbbc9">
              <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-2049" data-mp="54ife2rxujyi" />
              RD Ready study space
            </p>
            <h1 className="max-w-2xl text-5xl font-black tracking-[-0.05em] text-slate-900 sm:text-6xl" data-id="element-2050" data-mp="26xziqu91cyng">
              CNSC Exam Prep,
              <span className="block text-[#0b8776]" data-id="element-2051" data-mp="2wn6kmsdn4ny9">
                designed for clinical confidence.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-slate-700" data-id="element-2052" data-mp="30p9x9kc1pyl4">
              Focused nutrition support review, tied to your RD Ready account.
              Unlock CNSC Exam Prep with a one-time $150 purchase.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-2053" data-mp="3fliuyhgag3vf">
              <Link to={hasAccess ? getStudyUrl(PROGRAM_ID) : getCheckoutUrl(PROGRAM_ID)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0b8776] px-6 py-3.5 text-base font-bold text-white transition hover:bg-[#086f61] focus:outline-none focus:ring-4 focus:ring-[#0b8776]/30" data-id="element-2054" data-mp="3jmb6xigjomve">
                {hasAccess ? 'Open study' : 'Unlock CNSC Prep · $150'}
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-2055" data-mp="1rmykzmasfnw8" />
              </Link>
              {!hasAccess && <Link to="/login" state={{
              from: getCheckoutUrl(PROGRAM_ID)
            }} className="inline-flex items-center justify-center rounded-full border-2 border-slate-900 px-6 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-900 hover:text-white focus:outline-none focus:ring-4 focus:ring-slate-900/20" data-id="element-2056" data-mp="1mnm0h87e2swt">
                  Sign in to purchase
                </Link>}
            </div>
            <p className="mt-4 text-sm font-medium text-slate-600" data-id="element-2057" data-mp="20mbx6mqaqqkn">
              One-time $150 purchase · access stays with your RD Ready account.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.08
        }} className="relative rounded-[2rem] border-4 border-slate-900 bg-[#ffe36e] p-6 shadow-[8px_8px_0_#172033] sm:p-8" data-id="element-2058" data-mp="2t4zdlouclhfe">
            <div className="rounded-[1.4rem] border-2 border-slate-900 bg-white p-6" data-id="element-2059" data-mp="1yyuxsnlz8dvr">
              <div className="flex items-center justify-between" data-id="element-2060" data-mp="23wmvjy9ayu7v">
                <BookOpenCheckIcon className="h-9 w-9 text-[#0b8776]" aria-hidden="true" data-id="element-2061" data-mp="2rlfmthezcdw5" />
                <span className="rounded-full bg-[#d6f7f0] px-3 py-1 text-xs font-extrabold uppercase tracking-wider" data-id="element-2062" data-mp="2wk0prolm9boa">
                  CNSC exam
                </span>
              </div>
              <p className="mt-10 text-sm font-bold uppercase tracking-[0.16em] text-slate-500" data-id="element-2063" data-mp="2mtea5udwgl5l">
                Your study space
              </p>
              <p className="mt-2 text-3xl font-black tracking-tight" data-id="element-2064" data-mp="3fb0846ylvn8l">
                Build calm, clinical confidence.
              </p>
              <div className="mt-8 h-3 rounded-full bg-slate-100" data-id="element-2065" data-mp="13uzy2x59inwx">
                <div className="h-3 w-3/5 rounded-full bg-[#0b8776]" data-id="element-2066" data-mp="yxq2t0v6zi16" />
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-600" data-id="element-2067" data-mp="1dlul43qc1rjf">
                Focused questions, flashcards, and topic review.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-24" data-id="element-2068" data-mp="kwnfylwdi3p7">
        <div className="max-w-2xl" data-id="element-2069" data-mp="fylvbwjatwbm">
          <p className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#0b8776]" data-id="element-2070" data-mp="fznhaw1us384">
            Built for momentum
          </p>
          <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] sm:text-4xl" data-id="element-2071" data-mp="tvsalh4tdcw3">
            Keep your CNSC prep focused and flexible.
          </h2>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2072" data-mp="25bz0tp35dqeb">
          {studyFeatures.map(({
          icon: Icon,
          title,
          description
        }) => <article key={title} className="rounded-3xl border-2 border-slate-900 bg-white p-6 shadow-[4px_4px_0_#172033]" data-id="element-2073" data-mp="21e1d6qp0zcd1">
              <Icon className="h-8 w-8 text-[#0b8776]" aria-hidden="true" data-id="element-2074" data-mp="12yko71v75k1v" />
              <h3 className="mt-5 text-xl font-black" data-id="element-2075" data-mp="17ypa57evj6zh">{title}</h3>
              <p className="mt-2 leading-7 text-slate-600" data-id="element-2076" data-mp="1c0k7y1xlj28i">{description}</p>
            </article>)}
        </div>
      </section>

      <section className="bg-[#172033] px-5 py-16 text-white sm:px-8" data-id="element-2077" data-mp="fbcl8ql8qn30">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-7 rounded-[2rem] border-2 border-white/30 bg-[#274458] p-8 sm:p-10 lg:flex-row lg:items-center" data-id="element-2078" data-mp="k94o2fnfbtlz">
          <div data-id="element-2079" data-mp="t1oewtxh4tw9">
            <h2 className="text-3xl font-black tracking-[-0.04em]" data-id="element-2080" data-mp="t1g4d8otq0o8">
              Open your CNSC study space.
            </h2>
            <p className="mt-3 max-w-xl text-lg text-slate-200" data-id="element-2081" data-mp="3so4x1raz2bju">
              {hasAccess ? 'Your CNSC Exam Prep program is unlocked.' : 'Unlock your CNSC study space for a one-time $150 purchase.'}
            </p>
          </div>
          <Link to={hasAccess ? getStudyUrl(PROGRAM_ID) : getCheckoutUrl(PROGRAM_ID)} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#ffe36e] px-6 py-3.5 font-bold text-slate-900 transition hover:bg-white focus:outline-none focus:ring-4 focus:ring-[#ffe36e]/40" data-id="element-2082" data-mp="3k85t1fc6gspx">
            {hasAccess ? 'Open study' : 'Unlock for $150'}
            <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-2083" data-mp="31hxw5ez2myi6" />
          </Link>
        </div>
      </section>
    </div>;
}